import { makeAutoObservable } from 'mobx'
import { MessageFactory } from './../../shared/factories'
import UserProfileService from './../../shared/services/UserProfile.service'
import AdminApi from './../../api/admin-api/admin-api'
import MessageService from './../../shared/services/Message.service'
import moment from 'moment'

const timezoneOffset = new Date().getTimezoneOffset()

const getToday = () => moment().startOf('day')
const getLastDate = () => moment().subtract(7, 'days').endOf('day')

const correctCategory = category => {
  switch ((category = `${category || ''}`.trim().toLowerCase())) {
    case 'dashboard':
    case 'message':
      return 'dashboard'
    case 'leader':
    case 'leaders':
      return 'leader'
    case 'division':
      return 'division'
    default:
      return null
  }
}

class MessagingStore {
  Alert = null

  Readonly = {
    dashboard: null,
    leader: null,
    division: null,
  }

  Message = {
    dashboard: null,
    leader: null,
    division: null,
  }

  Messages = {
    dashboard: [],
    leader: [],
    division: [],
  }

  pagination = {
    dashboard: { page: 0, per_page: 10 },
    leader: { page: 0, per_page: 10 },
    division: { page: 0, per_page: 10 },
  }

  isSaving = false
  isDeleting = false
  isFetching = {
    _id: null,
    alert: false,
    dashboard: false,
    leader: false,
    division: false,
  }
  hasFetched = {
    alert: false,
    dashboard: false,
    leader: false,
    division: false,
  }

  lastDivisionId = null

  canUpdate = false
  canDelete = false
  errors = {}

  constructor() {
    makeAutoObservable(this)
  }

  fetchAlert = async () => {
    this.isFetching.alert = false
    this.Alert = false

    const response = await AdminApi.getMessageBoardAlertDataAdmin()
    this.Alert = response && response?.data?.data

    this.hasFetched.alert = true
    this.isFetching.alert = false
  }

  fetchMessage = async msgId => {
    if (msgId && (!this.isFetching._id || this.isFetching._id !== parseInt(msgId))) {
      this.isFetching._id = parseInt(msgId)

      const Message = msgId && (await MessageFactory.findById(msgId)),
        category = correctCategory(Message?.get('category'))
      this.isFetching[category] = true

      this.Message[category] = Message?.get('category') ? Message : null

      this.isFetching[category] = false
      return Message
    }
  }

  fetchMessagesByCategory = async (category, divisionId) => {
    category = category === 'leaders' ? 'leader' : category
    category = category === 'messages' ? 'dashboard' : category
    const search = { category }

    if ((divisionId = divisionId && !isNaN(divisionId) ? parseInt(divisionId) : null)) search.division_id = divisionId

    this.isFetching[category] = true

    const today = getToday()
    const lastDate = getLastDate()

    let { models, pagination } = await MessageFactory.search({
      search,
      pagination: this.pagination[category],
    })
    models = models.filter(
      Msg => !Msg.get('deleted_at') && (!Msg.get('start_date') || moment(Msg.get('start_date')).isSameOrBefore(today))
    )

    if (pagination && parseInt(pagination?.page) === 0) {
      this.Readonly[category] = models
        .filter(Msg => {
          return (
            !Msg.get('deleted_at') &&
            (Msg.get('end_date')
              ? moment(new Date(new Date(Msg.get('end_date')).getTime() + timezoneOffset * 60000)).isAfter(today)
              : moment(
                  new Date(new Date(Msg.get('start_date') || Msg.get('created_at')).getTime() + timezoneOffset * 60000)
                ).isAfter(lastDate))
          )
        })
        .shift()
      // models = models.filter((Msg) => Msg.id() !== this.Readonly[category]?.id())
    }

    this.Messages[category] = models
    this.pagination[category] = pagination
    this.hasFetched[category] = true
    if (category === 'division') this.lastDivisionId = search.division_id ?? null
    this.isFetching[category] = false
  }

  validate = category => {
    this.errors = {}

    const Message = this.Message[category]

    if (!Message.get('title') || !`${Message.get('title')}`.trim())
      this.errors.msgTitle = 'The message title is required.'

    if (!Message.get('category') || !`${Message.get('category')}`.trim())
      this.errors.msgCategory = 'The message category is required.'

    if (!Message.get('content') || !`${Message.get('content')}`.trim())
      this.errors.msgContent = 'The message content is required.'

    return !Object.keys(this.errors).length
  }

  onSubmit = async category => {
    if (this.isFetching[category] || this.isSaving) return false

    if (!this.validate(category)) return false

    const Message = this.Message[category]
    if (!Message.get('category')) Message.set('category', category)

    this.isSaving = true

    if (Message.get('start_date') && typeof Message.get('start_date') === 'object')
      Message.set('start_date', moment(Message.get('start_date')).format('YYYY-MM-DD'))
    if (Message.get('start_date') && typeof Message.get('start_date') === 'string')
      Message.set('start_date', new Date(Message.get('start_date')).toISOString().split('T')[0])
    if (Message.get('end_date') && typeof Message.get('end_date') === 'object')
      Message.set('end_date', moment(Message.get('end_date')).format('YYYY-MM-DD'))
    if (Message.get('end_date') && typeof Message.get('end_date') === 'string')
      Message.set('end_date', new Date(Message.get('end_date')).toISOString().split('T')[0])

    await Message.save()

    this.isSaving = false

    return true
  }

  onDelete = async category => {
    const Message = this.Message[(category = correctCategory(category))]

    this.isDeleting = true

    if (Message?.isNew() === false && Message?.id() > 0) {
      const msgId = Message.id()
      await MessageService.update(msgId, {
        deleted_by: UserProfileService.getUserId(true),
        deleted_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
      })
      this.hasFetched[category] = false
      this.Messages[category] = []
      this.Readonly[category] = null
    }

    this.isDeleting = false

    this.Message[category] = null
  }

  checkUserCanModify = async () => {
    this.canUpdate = false
    this.canDelete = false

    try {
      let permitted = await Promise.all([
        UserProfileService.canUser('messages.dashboard.update'),
        UserProfileService.canUser('messages.dashboard.delete'),
      ])
      this.canUpdate = permitted.shift()
      this.canDelete = permitted.shift()
    } catch (ex) {
      console.error('ERROR: Failed to check user permissions in MessageFormComponent.  ', ex)
    }
  }

  createMessage = async category => {
    // 'id',
    // 'title',
    // 'content',
    // 'category',
    // 'division_id',
    // 'deleted_by',
    // 'created_by',
    // 'updated_by',
    // 'start_date',
    // 'end_date',
    // 'deleted_at',
    // 'created_at',
    // 'updated_at',

    if (category) {
      this.Message[category] = await MessageFactory.create({
        category,
        created_by: UserProfileService.getUserId(),
        updated_by: UserProfileService.getUserId(),
        title: '',
        content: '',
        division_id: null,
        start_date: null,
        end_date: null,
      })
    }
  }

  updateAlert = async () => {
    if (!this.Alert?.alert_type || !this.Alert?.message || !`${this.Alert.message}`.length)
      throw new Error('Please complete all fields before attempting to save')

    const { alert_type, active, message } = this.Alert,
      data = {
        alert_type,
        active: parseInt(active),
        message,
      }

    this.isSaving = true
    this.isFetching.alert = true

    await AdminApi.updateMessageBoard(data)

    this.isSaving = false
    this.isFetching.alert = false

    return true
  }
}

export default new MessagingStore()
